<script setup lang="ts">
import { tpgIconSearch } from '@tpg/grammar-web-icons';
import Fuse from 'fuse.js/basic';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarCheckbox,
  GrammarFooter,
  GrammarLocalLoader,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarSecondaryButton,
  GrammarText,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

import type { MessageState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useForwardMessageStore } from '#nora-chat-ui/stores/forward-message-store';

interface ForwardMessageProps {
  messageState: MessageState;
}

const props = defineProps<ForwardMessageProps>();
const { t } = useI18n();
const conversationStore = useConversationStore();
const forwardMessageStore = useForwardMessageStore();

const { sortedConversation, getConversationTitle }
  = storeToRefs(conversationStore);
const { isSending } = storeToRefs(forwardMessageStore);

const selectedItems = ref<string[]>([]);
const searchQuery = ref('');

const searchableConversations = computed(() => sortedConversation.value.map(conversationState => ({
  conversationState,
  title: getConversationTitle.value(conversationState.conversationListResult.id) ?? '',
})));

const fuse = computed(() => new Fuse(searchableConversations.value, {
  keys: ['title'],
  threshold: 0.4,
}));

const filteredConversations = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) {
    return sortedConversation.value;
  }

  return fuse.value.search(query).map(result => result.item.conversationState);
});

const showModal = computed(() => {
  return (
    forwardMessageStore.showForwardMessageModal
    && forwardMessageStore.messageId === props.messageState.message.id
  );
});

function toggleConversationSelection(conversationId: string) {
  if (selectedItems.value.includes(conversationId)) {
    selectedItems.value = selectedItems.value.filter(
      id => id !== conversationId,
    );
  }
  else {
    selectedItems.value.push(conversationId);
  }
}

function close() {
  forwardMessageStore.showForwardMessageModal = false;
  selectedItems.value = [];
  searchQuery.value = '';
}

async function confirm() {
  await forwardMessageStore.forwardMessages(selectedItems.value);

  selectedItems.value = [];
  searchQuery.value = '';
  forwardMessageStore.showForwardMessageModal = false;
}
</script>

<template>
  <GrammarModal
    :is-open="showModal"
    :label="t('forwardingMessage.forwardingMessagePopupTitle')"
    :show-close-button="false"
    @close="close"
  >
    <template #content>
      <div v-if="!isSending" class="w-[min(80vw,24rem)]">
        <GrammarTextField
          v-model:value="searchQuery"
          :clear-button="true"
          :leading-icon-template="tpgIconSearch"
          :placeholder="t('common.search')"
          :form-associated="false"
          @change="searchQuery = $event"
        />
        <div class="h-[min(60vh,28rem)] overflow-y-auto overflow-x-hidden overscroll-contain px-1">
          <div
            v-for="conversationState in filteredConversations"
            :key="conversationState.conversationListResult.id"
            class="flex items-center py-1"
          >
            <GrammarCheckbox
              :checked="selectedItems.includes(conversationState.conversationListResult.id)"
              :label="getConversationTitle(conversationState.conversationListResult.id)"
              @change="toggleConversationSelection(conversationState.conversationListResult.id)"
            />
          </div>
          <div v-if="searchQuery.trim() && !filteredConversations.length" class="py-4 text-center text-sm">
            <GrammarText :text="t('noResults', [searchQuery.trim()])" />
          </div>
        </div>
      </div>
      <div v-else class="flex">
        <div class="m-auto">
          <GrammarLocalLoader />
        </div>
      </div>
    </template>

    <template #footer>
      <GrammarFooter>
        <template #left-button>
          <GrammarSecondaryButton :label="t('common.cancel')" @click="close" />
        </template>
        <template #right-button>
          <GrammarPrimaryButton :label="t('common.ok')" @click="confirm" />
        </template>
      </GrammarFooter>
    </template>
  </GrammarModal>
</template>
